// @steered AudibleSecurityContext 1.2 2026-09-21
import React, { useState, useEffect } from 'react';
import { Amplify, Hub } from '@aws-amplify/core';
import config from './aws-exports';
import { getCurrentUser } from 'aws-amplify/auth';
import { signInWithRedirect } from 'aws-amplify/auth';
import { fetchAuthSession } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';
import './App.css';
import './dark-mode.css';
import { Row, Col } from "antd"
import G2S2ChatContainer, { useG2S2Tabs } from "./components/G2S2ChatContainer";
import DebugOverlay from "./components/DebugOverlay";
import SettingsOverlay from "./components/SettingsOverlay";
import AppetizePreview from "./components/AppetizePreview";
import G2S2Chat from "./components/G2S2Chat";
import { useClickCounter, isLateNight, createConfetti } from './utils/uiHelpers';

// Connect the frontend with the backend
Amplify.configure(config);

// Interface that describes an authenticated user
interface AuthUser{
  username: string;
  userId: string;
}

type NativeDebugPlatform = 'ios' | 'android';

const getLocalNativePreviewPlatform = (): NativeDebugPlatform | null => {
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const requestedPlatform = new URLSearchParams(window.location.search).get('nativePreview');
  return isLocalhost && (requestedPlatform === 'ios' || requestedPlatform === 'android')
    ? requestedPlatform
    : null;
};

const getLocalNativeOverrideLabel = (): string =>
  new URLSearchParams(window.location.search).get('nativeOverride')?.trim().slice(0, 256) || '';

/**
 * Localhost-only harness for the "Ready to Submit for Review" card:
 * `?reviewCardDemo=<clientId>` (e.g. AudiblePDP, AudibleApp_PDP) seeds a G2S2
 * chat session that already has a created record, so the review card and its
 * surface-specific preview tabs can be exercised without Federate auth or a
 * real Lambda round-trip. `&demoStage=` picks the moment: `greeting` (Lumi's
 * opening message), `review` (default, card ready to submit), `submitted`
 * (in review, past the propagation window so the check button shows), `live`
 * (verified in production). Never active off localhost.
 */
const REVIEW_CARD_DEMO_SESSION = 'review-card-demo';
const getLocalReviewCardDemoClientId = (): string | null => {
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const clientId = new URLSearchParams(window.location.search).get('reviewCardDemo')?.trim();
  return isLocalhost && clientId && /^[A-Za-z0-9_]{1,64}(:[A-Za-z0-9_]{1,32})?$/.test(clientId) ? clientId : null;
};
type ReviewCardDemoStage = 'review' | 'greeting' | 'submitted' | 'live';
const getLocalReviewCardDemoStage = (): ReviewCardDemoStage => {
  const stage = new URLSearchParams(window.location.search).get('demoStage');
  return stage === 'greeting' || stage === 'submitted' || stage === 'live' ? stage : 'review';
};
const seedReviewCardDemoSession = (demo: string, stage: ReviewCardDemoStage = 'review'): void => {
  const [clientId, applicationType] = demo.split(':');
  const storageKey = `g2s2-chat-${REVIEW_CARD_DEMO_SESSION}`;
  const label = 'IgniteUtils-Label-00000000-demo-0000-000000000000';
  const url = `https://www.audible.com/pd/B08G9PRS1K?g2s2StageVersionOverride={AudibleCBBProdNA:${label}}`;
  const set = (k: string, v: string) => localStorage.setItem(`${storageKey}-${k}`, v);
  const keys = ['messages', 'show-form', 'form-data', 'validation-url', 'show-validation', 'slack-ready',
    'session-config', 'preview-surface', 'g2s2-result', 'submission-status'];
  keys.forEach((k) => localStorage.removeItem(`${storageKey}-${k}`));
  if (stage === 'greeting') {
    // No saved messages → G2S2Chat shows Lumi's opening message.
    return;
  }
  set('messages', JSON.stringify([
    { role: 'user', content: `Demo: replace the CTA string for ${clientId} behind DEMO_WEBLAB_1435483: T1` },
    { role: 'assistant', content: `✓ Successfully created G2S2 record(s)!\n\nLabel: ${label}\nRecords: 6` },
  ]));
  set('g2s2-result', JSON.stringify({
    label, stageVersion: label.replace('Label', 'StageVersion'),
    records: [{
      table_name: 'audible_actads_display_data',
      keys: { abods_config_clientId: clientId, actads_display_data_treatment_condition: 'DEMO_WEBLAB_1435483:T1', actads_display_data_attribute_key: 'ctaText' },
      data: { value: 'adbl_buybox_secondary_cta_see_all_plans_t1' }, marketplace: '',
    }],
  }));
  const hourAgo = Date.now() - 60 * 60 * 1000;
  if (stage === 'submitted') set('submission-status', JSON.stringify({ submittedAt: hourAgo, liveAt: null, lastCheckedAt: null }));
  if (stage === 'live') set('submission-status', JSON.stringify({ submittedAt: hourAgo, liveAt: Date.now() - 5 * 60 * 1000, lastCheckedAt: Date.now() }));
  set('show-form', 'false');
  set('form-data', JSON.stringify({ asin: 'B08G9PRS1K', asinType: 'AUDIOBOOK', customerType: 'non-member', country: 'us', pageType: 'pdp' }));
  set('validation-url', url);
  set('show-validation', 'true');
  set('slack-ready', stage === 'review' ? 'true' : 'false');
  set('session-config', JSON.stringify({
    asin: 'B08G9PRS1K', asinType: 'AUDIOBOOK', customerType: 'non-member', marketplace: 'us',
    pageType: 'pdp', weblabTreatment: 'DEMO_WEBLAB_1435483:T1', clientId, applicationType: applicationType || null,
  }));
};

function App () {
  // Current user
  const [user, setUser] = useState<AuthUser | null>(null);
  const [debugNativePlatform, setDebugNativePlatform] = useState<NativeDebugPlatform | null>(getLocalNativePreviewPlatform);
  const [debugNativeOverrideLabel, setDebugNativeOverrideLabel] = useState(getLocalNativeOverrideLabel);
  const [reviewCardDemoClientId] = useState<string | null>(() => {
    const clientId = getLocalReviewCardDemoClientId();
    if (clientId) seedReviewCardDemoSession(clientId, getLocalReviewCardDemoStage());
    return clientId;
  });
  // For sidebar collapse
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  // For smooth transitions
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  // Debug features
  const [isDebugUser, setIsDebugUser] = React.useState(false);
  const [debugOverlayOpen, setDebugOverlayOpen] = React.useState(false);
  const [settingsOverlayOpen, setSettingsOverlayOpen] = React.useState(false);
  const [debugFeatures, setDebugFeatures] = React.useState({
    browserPreview: false,
    showSessionIds: false
  });
  
  const [showFoundMessage, setShowFoundMessage] = useState(false);
  const [showNightOwl] = useState(isLateNight);
  
  const handleFooterClick = useClickCounter(7, () => {
    createConfetti();
    setShowFoundMessage(true);
    setTimeout(() => setShowFoundMessage(false), 3000);
  });
  
  // G2S2 tab management - lifted to App level for top nav rendering
  const g2s2Tabs = useG2S2Tabs();
  
  const [userSettings, setUserSettings] = React.useState(() => {
    const saved = localStorage.getItem('user-settings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse user settings', e);
      }
    }
    return {
      theme: 'warm-beige',
      notificationSounds: true
    };
  });

  // Save settings to localStorage
  React.useEffect(() => {
    localStorage.setItem('user-settings', JSON.stringify(userSettings));
  }, [userSettings]);


  // Check if user is debug user
  React.useEffect(() => {
    const checkDebugUser = async () => {
      try {
        const { idToken } = (await fetchAuthSession({ forceRefresh: false })).tokens ?? {};
        if (idToken) {
          const email = idToken.payload.email as string;
          if (email === 'pateeart@audible.com') {
            setIsDebugUser(true);
          }
        }
      } catch (error) {
        console.log('Error checking debug user:', error);
      }
    };
    if (user) {
      checkDebugUser();
    }
  }, [user]);

  const handleToggleDebugFeature = (feature: string) => {
    setDebugFeatures(prev => ({
      ...prev,
      [feature]: !prev[feature as keyof typeof prev]
    }));
  };

  const handleToggleSetting = (setting: string) => {
    setUserSettings((prev: any) => ({
      ...prev,
      [setting]: !prev[setting as keyof typeof prev]
    }));
  };

  // This is executed on the first render of the page
  useEffect(() => {
    if (debugNativePlatform || reviewCardDemoClientId) {
      return;
    }

    const handleLogin = async () => {
      try {
        // Check if the user is already authenticated. If the user is already authenticated,
        // then we will get an error message (which is ok)
        // If not authenticated, this will automatically sign the user in with Amazon Federate
        await signInWithRedirect({ provider: { custom: 'FederateOIDC' } });
      } catch (error) {
        console.log('Error signing in:', error);
        // renderApp(); // Render the app if sign-in fails
        getUser().then((userData: any) => {
          setUser(userData);
        });
      }
    };

    // Listens for any new events
    const listener = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        // New event that user is signed in, then set the user hook
        case 'signedIn':
          getUser().then((userData: any) => {
            setUser(userData);
          });
          break;
        // New event that the user is signed out, then update the user hook accordingly
        case 'signedOut':
          setUser(null);
          break;
        // In the event that the sign in led to a failure, log the error and the event
        case 'signInWithRedirect_failure':
          console.log('Sign in failure', payload.event);
          break;
      }
    });

    // Gets the current user that has been authenticated
    const getUser = async () => {
      try {
        const userData = await getCurrentUser();
        return userData;
      } catch {
        return console.log('Not signed in');
      }
    };

    // calls the handleLogin() function to handle the login
    handleLogin();

    return () => {
      listener();
    };
  }, [debugNativePlatform, reviewCardDemoClientId]);

  return (
    <div className={`app-container theme-${userSettings.theme || 'warm-beige'} ${userSettings.theme === 'dark' ? 'dark-mode' : ''}`}>
      {reviewCardDemoClientId ? (
          <div className="native-preview-debug-page" data-testid="review-card-demo">
            <header className="native-preview-debug-toolbar">
              <div className="native-preview-debug-brand">
                <strong>QuickLab review card</strong>
                <span>Local test harness — clientId {reviewCardDemoClientId}</span>
              </div>
              <div className="native-preview-debug-actions">
                <div className="native-preview-debug-platforms">
                  {['AudiblePDP', 'AudibleListView', 'AudibleApp_PDP', 'AudibleApp_PDP:Apple_App', 'AudibleApp_PDP:Android_App'].map((id) => (
                    <button
                      key={id}
                      type="button"
                      className={reviewCardDemoClientId === id ? 'selected' : ''}
                      onClick={() => { window.location.search = `?reviewCardDemo=${id}`; }}
                    >
                      {id}
                    </button>
                  ))}
                </div>
                <a href="/">Back to QuickLab</a>
              </div>
            </header>
            <G2S2Chat
              key={reviewCardDemoClientId}
              sessionId={REVIEW_CARD_DEMO_SESSION}
              tabName="Review card demo"
              debugFeatures={{ browserPreview: true, showSessionIds: false }}
            />
          </div>
      ) : debugNativePlatform ? (
          <div className="native-preview-debug-page">
            <header className="native-preview-debug-toolbar">
              <div className="native-preview-debug-brand">
                <strong>QuickLab native preview</strong>
                <span>Local test harness</span>
              </div>
              <label className="native-preview-debug-override">
                <span>G2S2 override</span>
                <input
                  maxLength={256}
                  onChange={(event) => setDebugNativeOverrideLabel(event.target.value)}
                  placeholder="AudibleCBBProdNA:IgniteUtils-Label-…"
                  spellCheck={false}
                  type="text"
                  value={debugNativeOverrideLabel}
                />
              </label>
              <div className="native-preview-debug-actions" aria-label="Preview platform">
                <div className="native-preview-debug-platforms">
                  <button
                    type="button"
                    className={debugNativePlatform === 'ios' ? 'selected' : ''}
                    onClick={() => setDebugNativePlatform('ios')}
                  >
                    iOS
                  </button>
                  <button
                    type="button"
                    className={debugNativePlatform === 'android' ? 'selected' : ''}
                    onClick={() => setDebugNativePlatform('android')}
                  >
                    Android
                  </button>
                </div>
                <a href="/">Back to QuickLab</a>
              </div>
            </header>
            <div className="chat-with-browser-container native-preview-debug-container">
              <AppetizePreview
                key={debugNativePlatform}
                platform={debugNativePlatform}
                overrideLabel={debugNativeOverrideLabel}
                standalone
                tabName={`${debugNativePlatform === 'ios' ? 'iOS' : 'Android'} App Preview`}
              />
            </div>
          </div>
      ) : user != null ? (
          <div className="app-wrapper fade-in">
            <Row className="main-content-row">
              <Col
                className={`sidebar-col ${sidebarCollapsed ? 'collapsed' : ''}`}
                span={4}
              >
                <div className="sidebar-wrapper">
                  <div className="sidebar-header">
                    <div className="sidebar-brand">
                      <img src="/logo.png" alt="QuickLab" className="sidebar-logo" />
                      <span className="sidebar-title">QuickLab</span>
                    </div>
                    <button className="sidebar-collapse-btn" onClick={() => setSidebarCollapsed(true)} title="Close sidebar">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="3"/>
                        <line x1="9" y1="3" x2="9" y2="21"/>
                      </svg>
                    </button>
                  </div>

                  <button className="sidebar-new-chat-btn" onClick={g2s2Tabs.addNewChat} title="New chat">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                    </svg>
                    New chat
                  </button>

                  <div className="sidebar-chats">
                    {g2s2Tabs.chatInstances.map((chat) => {
                      const isActive = g2s2Tabs.activeTabId === chat.id;
                      return (
                        <div
                          key={chat.id}
                          className={`sidebar-chat-item ${isActive ? 'active' : ''}`}
                          onClick={() => g2s2Tabs.handleTabSwitch(chat.id)}
                        >
                          {g2s2Tabs.editingId === chat.id ? (
                            <input
                              type="text"
                              className="sidebar-chat-input"
                              value={g2s2Tabs.editingName}
                              onChange={(e) => g2s2Tabs.setEditingName(e.target.value)}
                              onBlur={() => g2s2Tabs.saveEdit(chat.id)}
                              onKeyDown={(e) => g2s2Tabs.handleKeyDown(e, chat.id)}
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                            />
                          ) : (
                            <span
                              className="sidebar-chat-label"
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                g2s2Tabs.startEditing(chat.id, chat.name);
                              }}
                              title="Double-click to rename"
                            >
                              {chat.name}
                            </span>
                          )}
                          {g2s2Tabs.chatInstances.length > 1 && (
                            <button
                              className="sidebar-chat-close"
                              onClick={(e) => {
                                e.stopPropagation();
                                g2s2Tabs.removeChat(chat.id);
                              }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {g2s2Tabs.archivedChats.length > 0 && (
                    <div className="sidebar-archived">
                      <div className="sidebar-archived-header">Archived</div>
                      {g2s2Tabs.archivedChats.map((chat) => (
                        <div
                          key={chat.id}
                          className="sidebar-chat-item archived"
                        >
                          <span className="sidebar-chat-label">{chat.name}</span>
                          <div className="sidebar-archived-actions">
                            <button
                              className="sidebar-archived-btn restore"
                              onClick={() => g2s2Tabs.restoreChat(chat.id)}
                              title="Restore"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                            </button>
                            <button
                              className="sidebar-archived-btn delete"
                              onClick={() => g2s2Tabs.deleteArchivedChat(chat.id)}
                              title="Delete permanently"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="sidebar-bottom">
                    <button className="sidebar-action-btn" onClick={() => setSettingsOverlayOpen(true)}>
                      Settings
                    </button>
                    {isDebugUser && (
                      <button className="sidebar-action-btn" onClick={() => setDebugOverlayOpen(true)}>
                        Debug
                      </button>
                    )}
                    <a
                      className="sidebar-action-btn"
                      href="https://w.amazon.com/bin/view/Audible/ConsumerDomain/Vamos/Orca"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Audible ORCA Team ↗
                    </a>
                    <div className="sidebar-footer">
                      <span className="sidebar-credit" onClick={handleFooterClick}>
                        Built with care by @pateeart
                        {showNightOwl && <span className="night-owl-badge">Night Owl</span>}
                      </span>
                    </div>
                  </div>
                </div>
              </Col>
              <Col span={20} className={`content-col ${sidebarCollapsed ? 'expanded' : ''}`}>
                {sidebarCollapsed && (
                  <div className="top-bar">
                    <button
                      className="sidebar-toggle"
                      onClick={() => setSidebarCollapsed(false)}
                      aria-label="Open sidebar"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="3"/>
                        <line x1="9" y1="3" x2="9" y2="21"/>
                      </svg>
                    </button>
                  </div>
                )}
                <div className={`content-wrapper ${isTransitioning ? 'transitioning' : ''}`}>
                  <div className={`page-content ${!isTransitioning ? 'fade-in-content' : 'fade-out-content'}`} key="g2s2-chat">
                    <G2S2ChatContainer debugFeatures={debugFeatures} userSettings={userSettings} tabState={g2s2Tabs} />
                  </div>
                </div>
                <SettingsOverlay
                  isOpen={settingsOverlayOpen}
                  onClose={() => setSettingsOverlayOpen(false)}
                  settings={userSettings}
                  onToggleSetting={handleToggleSetting}
                  onChangeTheme={(theme: string) => setUserSettings((prev: any) => ({ ...prev, theme }))}
                />
                <DebugOverlay
                  isOpen={debugOverlayOpen}
                  onClose={() => setDebugOverlayOpen(false)}
                  features={debugFeatures}
                  onToggleFeature={handleToggleDebugFeature}
                />
              </Col>
            </Row>
            {showFoundMessage && <div className="found-message">Whale hello there! 🐋</div>}
          </div>
          ) : (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-text">Loading your workspace...</p>
              </div>
          )}
    </div>
  );
}

export default App;
